// @ts-check
import store from '@/store'
import { getUserData } from '@/utils/userData'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
// Sepetin hangi müşteriye ait olduğu: plasiyerde seçili müşteri, diğer kullanıcılarda kendi müşteri kodu.
export const getOrderOwner = () => {
  const userData = getUserData()
  if (['30', '110'].includes(String(userData.profile))) return store.state.plasiyerCustomer || null

  return userData.customer_id || null
}

let itemSeq = 0

// Sepet satırı için benzersiz id (eski rastgele 1-10 id'si çakışıp yanlış satırı siliyordu).
export const newItemId = () => `${Date.now()}-${(itemSeq += 1)}`

export const useOrderStore = defineStore({
  id: 'order',
  state: () => ({
    rawItems: useStorage('rawItems', []),
    directionGlass: null,
  }),
  getters: {
    items: state =>
      state.rawItems.reduce((items, item) => {
        const userData = getUserData()
        const deepDiscRate = Number(userData.deep_disc_rate) || 0

        var discountTxt = ''
        var discAmountSum = 0
        ;(Array.isArray(item.discount) ? item.discount : []).forEach(discAmount => {
          if (discAmount === undefined || discAmount === null) return
          discountTxt += `${discAmount} + `
          discAmountSum += discAmount
        })

        // item.price birim fiyattır; satır toplamları için qty ile çarpılır.
        // taxAmount/netAmount/subTotal backend'den zaten miktar dahil toplam
        // olarak geliyor, bunlara tekrar qty ile çarpılmaz.
        const tax = (item.price * item.tax_rate * item.qty) / 100

        const lineTotal = item.price * item.qty

        // discAmountSum backend'den gelen (fiyat bazlı) iskonto tutarıdır, kampanya
        // burada karışmaz — "İskonto" sütununda olduğu gibi gösterilir.
        const discAmountWithoutCampaign = discAmountSum

        // İskonto oranı = iskonto tutarı ÷ satır toplamı × 100
        const discRate = lineTotal > 0 ? (discAmountWithoutCampaign / lineTotal) * 100 : 0

        // Dip iskonto (kullanıcı profili + kampanya), iskonto UYGULANDIKTAN SONRAKİ
        // tutar üzerinden ayrı bir kademe olarak hesaplanır: (lineTotal - iskonto) × oran.
        // cmpDiscValueType === 1 => cmpDiscValue yüzde orandır.
        const netAfterDiscount = Math.max(lineTotal - discAmountWithoutCampaign, 0)
        const campaignDiscAmount =
          item.cmpDiscValueType === 1 && item.cmpDiscValue ? (netAfterDiscount * item.cmpDiscValue) / 100 : 0
        const deepDiscAmount = (netAfterDiscount * deepDiscRate) / 100
        const netAfterDeepDisc = Math.max(netAfterDiscount - deepDiscAmount - campaignDiscAmount, 0)
        const taxAfterDeepDisc = (netAfterDeepDisc * item.tax_rate) / 100

        items.push({
          id: item.id,
          prd_code: item.prd_code,
          title: item.title,
          qty: item.qty,
          price: item.price,
          discount: discountTxt.slice(0, -2) || '━',
          discRate: discRate,
          discountMsg: item.cmpDiscMsg,
          tax_rate: item.tax_rate,
          currency: item.currency,
          amount: lineTotal,
          rowAmount: lineTotal,
          discountPrice: discAmountWithoutCampaign,
          campaignDiscountPrice: campaignDiscAmount,
          deepDiscountPrice: deepDiscAmount + campaignDiscAmount,
          tax: tax,
          taxAmount: taxAfterDeepDisc,
          netAmount: netAfterDeepDisc,
          deep_disc_rate: deepDiscRate,
          customer_id: item.owner ?? userData.customer_id,
        })

        return items
      }, []),

    lines: state =>
      state.rawItems.map(({ prd_code, qty, type, brand, direction, fullname, axe }, index) =>
        type === 'stock'
          ? {
              line_num: index + 1,
              prod_type: type,
              prod_code: prd_code,
              qty: qty,
              brand: brand,
              direction:
                direction && typeof direction === 'object'
                  ? direction.value || 'SOL'
                  : direction || 'SOL',
              patient: fullname,
              axe: axe,
            }
          : {
              line_num: index + 1,
              prod_type: type,
              prod_code: prd_code,
              qty: qty,
            },
      ),

    subTotal() {
      return this.items.reduce((sum, a) => sum + a.amount, 0)
    },
    rowTotal() {
      return this.items.reduce((sum, a) => sum + a.rowAmount, 0)
    },

    discountPrice() {
      return this.items.reduce((sum, a) => sum + a.discountPrice, 0)
    },

    dipdiscountPrice() {
      // Dip iskonto (kullanıcı profili + kampanya) tutarı, satırların netAmount'ına
      // zaten uygulanmıştır (bkz. items getter). Burada bilgi satırında göstermek
      // için ayrıca toplanır.
      return this.items.reduce((sum, a) => sum + a.deepDiscountPrice, 0)
    },

    taxes() {
      // Her satırın taxAmount değeri artık dip iskonto sonrası tutar üzerinden
      // hesaplanmış olarak gelir (bkz. items getter).
      return this.items.reduce((sum, a) => sum + a.taxAmount, 0)
    },

    subTotals() {
      // netAmount artık dip iskonto düşülmüş Alt Toplam'dır (bkz. items getter).
      return this.items.reduce((sum, a) => sum + a.netAmount, 0)
    },

    total() {
      /** @type {any[]} */
      const items = this.items

      return items.reduce((sum, a) => sum + a.netAmount + a.taxAmount, 0)
    },
  },
  actions: {
    addItem(name) {
      this.rawItems.push({
        ...name,
        owner: getOrderOwner(),
        direction:
          name.direction && typeof name.direction === 'object'
            ? { ...name.direction, reverse: name.direction.reverse ? { ...name.direction.reverse } : undefined }
            : name.direction,
        discount: Array.isArray(name.discount) ? [...name.discount] : name.discount,
      })
    },

    // Sepet başka bir müşteriye aitse (müşteri değişti / eski taslak) temizler.
    clearIfForeignOwner() {
      const owner = getOrderOwner()
      if (owner === null) return false // plasiyerde müşteri henüz seçilmedi; seçilince tekrar kontrol edilir
      if (this.rawItems.some(item => item.owner !== owner)) {
        this.rawItems = []

        return true
      }

      return false
    },

    removeItem(id) {
      const index = this.rawItems.map(object => object.id).indexOf(id)
      if (index > -1) this.rawItems.splice(index, 1)
    },
  },
})
